import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getCheckoutStatus } from "@/lib/api";

const POLL_MAX = 12;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("loading"); // loading | paid | expired | error
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await getCheckoutStatus(sessionId);
        setData(res);
        if (res.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (res.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        if (attempts >= POLL_MAX) {
          setStatus("error");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        attempts += 1;
        if (attempts >= POLL_MAX) {
          setStatus("error");
        } else {
          timer = setTimeout(poll, POLL_INTERVAL);
        }
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#050505]" data-testid="payment-success-page">
      <div className="max-w-lg w-full bg-[#111] border border-[#27272A] rounded-3xl p-10 text-center relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#CCFF00]/20 blur-3xl" />
        <div className="relative">
          {status === "loading" && (
            <>
              <Loader2 className="w-12 h-12 text-[#CCFF00] animate-spin mx-auto" />
              <h1 className="font-display text-3xl font-bold mt-6">
                Confirming your payment…
              </h1>
              <p className="text-zinc-400 mt-2">
                Hang tight, this only takes a few seconds.
              </p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]/30 flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-[#CCFF00]" />
              </div>
              <h1 className="font-display text-3xl font-black tracking-tight mt-6">
                Payment <span className="text-gradient-neon">successful!</span>
              </h1>
              <p className="text-zinc-300 mt-3">
                Thank you for choosing bellso digital. We'll email you within
                24 hours to kick off your project.
              </p>
              {data?.amount_total ? (
                <div className="mt-6 inline-block px-4 py-2 rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]/30 font-mono-jb text-sm">
                  Paid: ${(data.amount_total / 100).toFixed(2)}{" "}
                  {String(data.currency || "usd").toUpperCase()}
                </div>
              ) : null}
            </>
          )}
          {status === "expired" && (
            <>
              <XCircle className="w-12 h-12 text-red-500 mx-auto" />
              <h1 className="font-display text-3xl font-bold mt-6">
                Session expired
              </h1>
              <p className="text-zinc-400 mt-2">
                Your checkout session expired. Please try again.
              </p>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle className="w-12 h-12 text-red-500 mx-auto" />
              <h1 className="font-display text-3xl font-bold mt-6">
                Something went wrong
              </h1>
              <p className="text-zinc-400 mt-2">
                We couldn't confirm payment. If you were charged, please email
                info@bellsodigital.com.
              </p>
            </>
          )}

          <Link to="/">
            <Button
              className="btn-neon rounded-none mt-8 px-8"
              data-testid="payment-back-home"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
